import React, { useMemo, useState, useEffect } from 'react';

const FIELD_LABELS = {
  full_name: 'Full Name',
  phone: 'Phone',
  national_id: 'National ID',
  license_number: 'License Number',
  license_expiry: 'License Expiry',
  nhif_number: 'NHIF Number',
};

const DEFAULT_FORM = {
  full_name: '',
  phone: '',
  national_id: '',
  license_number: '',
  license_expiry: '',
  nhif_number: '',
};

export default function DriverOnboardingGate({
  loading,
  status,
  initialProfile,
  submitting,
  onSubmit,
}) {
  const requiredFields = Array.isArray(status?.required_fields)
    ? status.required_fields
    : Object.keys(DEFAULT_FORM);

  const [form, setForm] = useState(DEFAULT_FORM);

  useEffect(() => {
    setForm({
      full_name: initialProfile?.full_name || '',
      phone: initialProfile?.phone || '',
      national_id: initialProfile?.national_id || '',
      license_number: initialProfile?.license_number || '',
      license_expiry: initialProfile?.license_expiry || '',
      nhif_number: initialProfile?.nhif_number || '',
    });
  }, [initialProfile]);

  const missingSet = useMemo(() => {
    const list = Array.isArray(status?.missing_fields) ? status.missing_fields : [];
    return new Set(list);
  }, [status?.missing_fields]);

  const handle = (e) => setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const submit = (e) => {
    e.preventDefault();
    const payload = requiredFields.reduce((acc, field) => {
      acc[field] = form[field] ?? '';
      return acc;
    }, {});
    onSubmit(payload);
  };

  if (loading) {
    return (
      <div className="card" style={{ maxWidth: 760, margin: '2rem auto' }}>
        <div className="card-header">
          <h3 style={{ margin: 0, fontFamily: 'var(--font-display)' }}>Loading onboarding</h3>
        </div>
        <div className="card-body">
          <p style={{ margin: 0, color: 'var(--gray-600)' }}>
            Checking your driver onboarding status...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="card" style={{ maxWidth: 760, margin: '2rem auto' }}>
      <div className="card-header">
        <h3 style={{ margin: 0, fontFamily: 'var(--font-display)' }}>Complete Driver Onboarding</h3>
      </div>
      <div className="card-body">
        <p style={{ marginTop: 0, color: 'var(--gray-600)' }}>
          Complete your driver profile before accessing dashboard data.
        </p>

        {missingSet.size > 0 && (
          <div
            style={{
              border: '1px solid var(--warning-border, #f59e0b)',
              borderRadius: 'var(--radius-md)',
              padding: '0.75rem 0.9rem',
              marginBottom: '1rem',
              fontSize: '0.86rem',
              color: 'var(--warning-text)',
              background: 'var(--warning-light)',
            }}
          >
            Missing fields: {Array.from(missingSet).map((f) => FIELD_LABELS[f] || f).join(', ')}
          </div>
        )}

        <form onSubmit={submit}>
          <div className="grid-2">
            <div className="form-group">
              <label className="form-label">Full Name *</label>
              <input className="form-control" name="full_name" value={form.full_name} onChange={handle} required />
            </div>
            <div className="form-group">
              <label className="form-label">Phone *</label>
              <input className="form-control" name="phone" value={form.phone} onChange={handle} required />
            </div>
          </div>

          <div className="grid-2">
            <div className="form-group">
              <label className="form-label">National ID *</label>
              <input className="form-control" name="national_id" value={form.national_id} onChange={handle} required />
            </div>
            <div className="form-group">
              <label className="form-label">NHIF Number *</label>
              <input className="form-control" name="nhif_number" value={form.nhif_number} onChange={handle} required />
            </div>
          </div>

          <div className="grid-2">
            <div className="form-group">
              <label className="form-label">License Number *</label>
              <input className="form-control" name="license_number" value={form.license_number} onChange={handle} required />
            </div>
            <div className="form-group">
              <label className="form-label">License Expiry *</label>
              <input className="form-control" type="date" name="license_expiry" value={form.license_expiry} onChange={handle} required />
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '0.5rem' }}>
            <button className="btn btn-primary" type="submit" disabled={submitting}>
              {submitting ? 'Saving...' : 'Complete Onboarding'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
